import { getQuickTagShortLabel, loadSuppliers, loadSuppliersByStructure, selectAllSuppliers, selectSuppliers, Supplier } from "./suppliersSlice";
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { AppId } from "../../app/appTypes";
import { showError } from "../notifications/notificationsSlice";
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Chip from '@mui/material/Chip';
import { Box, Dialog, Grid, Paper, TableContainer, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Localized, useLocalization } from '@fluent/react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from "react-router-dom";
import { LoadingContainer } from "../../app/LoadingContainer";
import { selectSettings } from "../settings/settingsSlice";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { CreateSupplierDialog } from "./dialogs/CreateSupplierDialog";
import { MultiSelectStructureComponent } from "../structure/MultiSelectStructureComponent";
import { GoToButton } from "../../app/components/GoToButton";
import { usePackage } from "../permissions/usePackage";

type DialogState =
    | { type: "none" }
    | { type: "create" }

type SupplierViewMode = "list" | "structure"

const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
    const navigate = useNavigate();

    const onSuccess = useCallback((supplier: Supplier) => {
        setter({ type: "none" })
        navigate(`/suppliers/edit/${supplier.id}`);
    }, [setter, navigate])
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null
        case "create":
            return <CreateSupplierDialog
                onSuccess={onSuccess}
                onCancel={onCancel}
            />
    }
}

export const Suppliers = () => {
    const { state } = useAppSelector(selectSuppliers)
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const rows = useAppSelector(state => selectAllSuppliers(state))
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })
    const [viewMode, setViewMode] = useState<SupplierViewMode>("list")
    const canCreate = usePackage("CompanySupplierDictionary", "ReadWrite")
    const [selectedStructureIds, setSelectedStructureIds] = useState<AppId[]>([])
    const [structureFilteredRows, setStructureFilteredRows] = useState<Supplier[] | null>(null)
    const [structureViewLoading, setStructureViewLoading] = useState(false)

    useEffect(() => {
        if (state.type === "empty" || state.type === "error") {
            setIsLoading(true);
            dispatch(loadSuppliers())
        } else {

            setIsLoading(false)
        }
    }, [state.type, dispatch])

    useEffect(() => {
        if (viewMode !== "structure" || selectedStructureIds.length === 0) {
            setStructureFilteredRows(null)
            setStructureViewLoading(false)
            return
        }

        let isCurrentRequest = true
        setStructureFilteredRows([])
        setStructureViewLoading(true)

        const loadFilteredSuppliers = async () => {
            const result = await dispatch(loadSuppliersByStructure(selectedStructureIds))
            if (!isCurrentRequest) {
                return
            }

            if (loadSuppliersByStructure.fulfilled.match(result)) {
                setStructureFilteredRows(result.payload)
            } else {
                dispatch(showError("error"))
            }

            setStructureViewLoading(false)
        }

        void loadFilteredSuppliers()
        return () => {
            isCurrentRequest = false
        }
    }, [dispatch, selectedStructureIds, viewMode])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleGoToClick = (id) => {
        navigate(`/suppliers/edit/${id}`)
    };

    const handleAddClick = () => {
        setDialog({ type: "create" })
    }

    const handleViewModeChange = useCallback((_: React.MouseEvent<HTMLElement>, mode: SupplierViewMode | null) => {
        if (!mode) {
            return
        }
        setViewMode(mode)
    }, [])

    const filteredRows = useMemo(() => {
        return viewMode === "structure" && selectedStructureIds.length > 0
            ? structureFilteredRows ?? rows
            : rows
    }, [rows, selectedStructureIds.length, structureFilteredRows, viewMode])

    const columns: GridColDef<Supplier, any, any>[] =
        [
            {
                field: 'shortName',
                headerName: l10n.getString('supplier-shortName', null, '_Nazwa skrócona'),
                flex: 1
            },
            {
                field: 'name',
                headerName: l10n.getString('supplier-name', null, '_Nazwa'),
                flex: 1
            },
            {
                field: 'supplierType',
                headerName: l10n.getString('supplier-type', null, '_Typ dostawcy'),
                flex: 1,
                valueGetter: (_, row) => {
                    const supplierType = row.supplierType as unknown
                    if (supplierType === 0 || supplierType === "0" || supplierType === null || supplierType === undefined || supplierType === "") {
                        return "-"
                    }

                    return l10n.getString(`supplier-type-${supplierType}`, null, `_${supplierType}`)
                }
            },
            {
                field: 'quickTags',
                headerName: l10n.getString('supplier-tags', null, '_Tagi'),
                flex: 1.4,
                sortable: false,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1 }}>
                        {(params.row.quickTags ?? []).slice(0, 3).map((tag) => {
                            const label = getQuickTagShortLabel(tag)
                            return <Chip key={tag.quickTagId} size="small" label={label} />
                        })}
                    </Box>
                )
            },
            {
                field: 'status',
                headerName: l10n.getString('status', null, '_Status'),
                flex: 1,
                renderCell: (params) => (
                    <Chip
                        size="small"
                        label={<Localized id={params.row.status.toLowerCase()}><span>_{params.row.status}</span></Localized>}
                        color={params.row.status === "ACTIVE" ? "success" : "error"}
                        variant="filled"
                        sx={{ fontWeight: 700, letterSpacing: 0.4 }}
                    />
                )
            },
            {
                field: 'actions',
                type: "actions",
                width: 75,
                renderCell: (params) => {
                    return (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                            <GoToButton onClick={() => handleGoToClick(params.row.id)} />
                        </Box>
                    )
                }
            }
        ];

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    const renderViewModeToggle = () => (
        <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            color="secondary"
            onChange={handleViewModeChange}
        >
            <ToggleButton value="list">
                <Tooltip title={l10n.getString("supplier-view-list", null, "_Lista")}>
                    <ViewListIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
            <ToggleButton value="structure">
                <Tooltip title={l10n.getString("supplier-view-structure", null, "_Struktura")}>
                    <AccountTreeIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    )

    const renderTableContent = () => (
        <>
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {renderViewModeToggle()}
            </Box>
            <DataGrid
                style={{ minHeight: 100, height: 'calc(100vh - 220px)' }}
                localeText={getCustomLocaleText()}
                rows={filteredRows}
                columns={columns}
                slots={{ toolbar: QuickSearchToolbar }}
                onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                initialState={{
                    sorting: { sortModel: [{ field: "shortName", sort: "asc" }] },
                }}
            />
            <Box sx={{ display: 'flex' }}>
                {canCreate && <Fab sx={{ margin: 1 }} onClick={handleAddClick} color="secondary" size="small">
                    <AddIcon />
                </Fab>}
            </Box>
        </>
    )

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {viewMode === "list" ? (
                    <div style={{ display: 'flex', height: '100%' }}>
                        <TableContainer component={Paper}>
                            {renderTableContent()}
                        </TableContainer>
                    </div>
                ) : (
                    <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
                        <Grid container spacing={2} sx={{ height: '100%' }}>
                            <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        height: '100%',
                                        overflow: 'auto',
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <MultiSelectStructureComponent
                                            expandedOnOpen={false}
                                            selectedIds={selectedStructureIds}
                                            onSelectionChange={setSelectedStructureIds}
                                            showSelectedChips={true}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                                <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    {structureViewLoading && <Box sx={{ p: 2 }}><LoadingContainer /></Box>}
                                    <Box sx={{ px: 1, pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                        {renderViewModeToggle()}
                                    </Box>
                                    <Box sx={{ flex: 1, minHeight: 0 }}>
                                        <DataGrid
                                            style={{ minHeight: 100, height: '100%' }}
                                            localeText={getCustomLocaleText()}
                                            rows={filteredRows}
                                            columns={columns}
                                            slots={{ toolbar: QuickSearchToolbar }}
                                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex' }}>
                                        {canCreate && <Fab sx={{ margin: 1 }} onClick={handleAddClick} color="secondary" size="small">
                                            <AddIcon />
                                        </Fab>}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}
                <Dialog open={dialog.type !== "none"} fullWidth maxWidth="md" keepMounted={false}>
                    <DialogContentDispatcher state={dialog} setter={setDialog} />
                </Dialog>
            </Box>
        </>
    );
}
